import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbCarousel, NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap';
import { Testimonial } from '../../models';

@Component({
    selector: 'pk-web-testimonial-carousel',
    templateUrl: './testimonial-carousel.component.html',
    styleUrl: './testimonial-carousel.component.scss',
    standalone: false
})
export class TestimonialCarouselComponent implements OnInit, AfterViewInit {
	@Input() testimonials: Testimonial[] = [];
	@ViewChild('testimonialCarousel') testimonialCarousel!: NgbCarousel;

	public currentTestimonialSlide = 0;
	public totalTestimonialSlides = 0;
	private videoUrlCache = new Map<string, SafeResourceUrl>();

	constructor(private sanitizer: DomSanitizer) {}

	ngOnInit(): void {
		this.totalTestimonialSlides = this.testimonials.length;
		this.currentTestimonialSlide = 0;
	}

	ngAfterViewInit(): void {
		// Initialize the current slide after view is ready
		if (this.testimonialCarousel && this.testimonials.length > 0) {
			this.updateCurrentSlide();
		}
	}

	private updateCurrentSlide(): void {
		if (!this.testimonialCarousel || !this.testimonialCarousel.activeId) {
			this.currentTestimonialSlide = 0;
			return;
		}

		// Find the index of the active slide by matching it to our testimonials array
		const activeId = this.testimonialCarousel.activeId.toString();
		// Extract the slide number from the ID (e.g., "ngb-slide-2" -> 2)
		const match = activeId.match(/(\d+)$/);
		if (match) {
			const slideNumber = parseInt(match[1], 10);
			// Ensure the slide number is within bounds of our testimonials array
			if (slideNumber >= 0 && slideNumber < this.testimonials.length) {
				this.currentTestimonialSlide = slideNumber;
			} else {
				this.currentTestimonialSlide = 0;
			}
		} else {
			this.currentTestimonialSlide = 0;
		}
	}

	onTestimonialSlideChange(event: NgbSlideEvent): void {
		// Update current slide based on the event
		if (event.current) {
			if (typeof event.current === 'string') {
				const match = event.current.match(/(\d+)$/);
				if (match) {
					const slideNumber = parseInt(match[1], 10);
					// Ensure the slide number is within bounds of our testimonials array
					if (slideNumber >= 0 && slideNumber < this.testimonials.length) {
						this.currentTestimonialSlide = slideNumber;
					}
				}
			} else if (typeof event.current === 'number') {
				if (event.current >= 0 && event.current < this.testimonials.length) {
					this.currentTestimonialSlide = event.current;
				}
			}
		} else {
			// Fallback to checking the carousel's activeId
			this.updateCurrentSlide();
		}
	}

	goToPreviousSlide(): void {
		if (this.testimonialCarousel) {
			this.testimonialCarousel.prev();
		}
	}

	goToNextSlide(): void {
		if (this.testimonialCarousel) {
			this.testimonialCarousel.next();
		}
	}

	hasVideo(testimonial: Testimonial): boolean {
		return !!(testimonial.video && testimonial.video.trim() !== '');
	}

	hasImage(testimonial: Testimonial): boolean {
		return !!(testimonial.image && testimonial.image.trim() !== '');
	}

	getSafeVideoUrl(testimonial: Testimonial): SafeResourceUrl | null {
		if (this.hasVideo(testimonial) && testimonial.video) {
			// Cache the sanitized URL to prevent iframe refreshes
			if (!this.videoUrlCache.has(testimonial.video)) {
				this.videoUrlCache.set(testimonial.video, this.sanitizer.bypassSecurityTrustResourceUrl(testimonial.video));
			}
			return this.videoUrlCache.get(testimonial.video)!;
		}
		return null;
	}
}
